import { usePlayerActions } from "@utils/player/core/usePlayer";
import { randomUUID } from "expo-crypto";
import { useEffect, useRef } from "react";
import LScreen from "@components/LScreen";
import LText from "@components/LText";
import LoadingIndicator from "./LoadingIndicator";
import { useMarkOnboardingCompleted } from "@hooks/trpc/useMarkOnboardingCompleted";
import { router, useLocalSearchParams } from "expo-router";
import LBox from "@components/LBox";
import { trackOnb, trackDirectImport } from "./analytics/trackOnboarding";
import { useOnboardingStore } from "./store/onboardingStore";
import {
  useMusicSyncSaveGenres,
  useMusicSync,
  musicSyncGetPlaylistSyncProgressQuery,
} from "@hooks/trpc/useMusicSync";
import { useData } from "../../runtime/provider";
import useRevenueCat from "@utils/revenuecat";

import { auth } from "@utils/firebase";
import { toast } from "sonner-native";
export default function LoadingScreen() {
  const data = useData();
  const { queryClient } = data;
  const playerActions = usePlayerActions();

  const { source = "pick-genres" } = useLocalSearchParams<{
    source?: string;
  }>();
  const journey = useOnboardingStore((s) => s.journey) ?? "signup";
  const trackFn = (
    screen: string,
    action: string,
    props?: Record<string, unknown>,
  ) =>
    journey === "direct-import"
      ? trackDirectImport(screen, action, props)
      : trackOnb(journey, screen, action, props);
  const { mutateAsync: completeOnboarding } = useMarkOnboardingCompleted();
  const { userHasActiveSubscription } = useRevenueCat({
    surface: "onboarding-loading",
  });
  const path = source === "pick-playlists" ? "playlists" : "genres";

  useEffect(() => {
    trackFn("loading", "view", { path });
  }, [journey, path]);

  const { mutateAsync: saveGenres } = useMusicSyncSaveGenres();
  const { mutateAsync: syncMusic } = useMusicSync();
  const importAttemptId = useRef<string | null>(null);

  useEffect(() => {
    const performOnboardingMutations = async () => {
      try {
        const mutations = [];
        const {
          selectedGenres,
          excludedPlaylists,
          streamingService,
          integrationUserUUID,
          clearOnboardingData,
        } = useOnboardingStore.getState();

        if (selectedGenres.length > 0) {
          mutations.push(
            saveGenres({
              genres: selectedGenres.map((g) => ({ id: g.id })),
            }),
          );
        }

        if (streamingService) {
          importAttemptId.current ??= randomUUID();
          mutations.push(
            syncMusic({
              importAttemptId: importAttemptId.current,
              excludedPlaylists,
              service: streamingService,
              integrationUserUUID,
            }).then(() =>
              queryClient.invalidateQueries({
                queryKey: musicSyncGetPlaylistSyncProgressQuery(data).queryKey,
              }),
            ),
          );
        }

        if (mutations.length > 0) {
          await Promise.all(mutations);
        }

        // Reload explore queue with new personalized recommendations.
        // userId stamps paginationUserId as OWNED by this user, so the
        // bootstrap's post-onboarding gated fetch sees the queue is already
        // theirs and does NOT force-replace the welcome queue again.
        await playerActions.loadForYouInitial({
          forceReplace: true,
          resetCursors: true,
          userId: auth.currentUser?.uid ?? undefined,
        });

        // Direct-import: user already onboarded, skip state mutation
        if (journey !== "direct-import") {
          await completeOnboarding();
        }

        clearOnboardingData();

        trackFn("loading", "completed", { path });

        // Direct-import skips paywall entirely. Already-entitled users skip
        // it too — asking a premium user to pay again is only reachable since
        // the WER1 handoff started sending pre-provisioned (and pre-imported,
        // per LIS-439 P6) subscribers through signup-journey onboarding.
        // Checked at decision time (not via the query hook) so a mid-flight
        // cache can't race the redirect; errors fall through to the paywall,
        // which stays the safe default for non-entitled users.
        const isPremium =
          journey === "direct-import"
            ? false
            : await userHasActiveSubscription().catch(() => false);
        router.replace(
          journey === "direct-import"
            ? "/profile"
            : isPremium
              ? "/"
              : "/paywall?onboarding=true",
        );
      } catch (error) {
        console.error("::onboarding mutations error::", error);
        const errorMessage =
          error instanceof Error ? error.message : "Unknown error";
        trackFn("loading", "error", { error: errorMessage });
        toast.error("Error setting up your profile");
        router.replace("/onboarding");
      }
    };

    performOnboardingMutations().catch((error) => {
      console.error("::performOnboardingMutations unhandled error::", error);
    });
  }, []);

  return (
    <LScreen safeArea>
      <LBox
        justifyContent="center"
        alignItems="center"
        gap="s2"
        style={{
          marginTop: "40%",
        }}
      >
        <LoadingIndicator />
        <LText variant="title-mid" fontWeight="600">
          Setting up your profile
        </LText>
        <LText color="greyText" fontWeight="500" variant="bodyL">
          Preparing your recommendations...
        </LText>
      </LBox>
    </LScreen>
  );
}
